import { selector } from '@/lib/selectors';

import type { GridPresetStateRecord } from '@/domain/documents/gridPreset/factories';
import type { GridLayout, GridLayoutChanges } from '@/domain/documents/gridPreset/schemas';
import {
  hasLayoutChanges,
  layoutChanges,
  layoutInGrid,
  withPresetPartsNotInGrid,
} from '@/domain/documents/gridPreset/convert';

type RootState = { documentsGridPreset: GridPresetStateRecord };

// Stable fallbacks: readers get the same reference while nothing is resolved.
const NO_FILTERS: GridLayout['filters'] = Object.freeze({}) as GridLayout['filters'];
const NO_SORTING: GridLayout['sorting'] = Object.freeze([]) as unknown as GridLayout['sorting'];
const NO_CHANGES: GridLayoutChanges = Object.freeze({});

const gridPreset = (state: RootState): GridPresetStateRecord => state.documentsGridPreset;

export const gridPresetCompanyIdSelector = selector<RootState, string | null>(
  gridPreset,
  (l: GridPresetStateRecord) => l.companyId,
);
export const gridPresetIdSelector = selector<RootState, string>(gridPreset, (l: GridPresetStateRecord) => l.presetId);
export const gridPresetSharedIdSelector = selector<RootState, string | null>(
  gridPreset,
  (l: GridPresetStateRecord) => l.sharedPresetId,
);
export const gridPresetPersistableSelector = selector<RootState, boolean>(
  gridPreset,
  (l: GridPresetStateRecord) => l.persistable,
);
export const gridPresetRevisionSelector = selector<RootState, number>(
  gridPreset,
  (l: GridPresetStateRecord) => l.revision,
);
export const gridPresetSavedSelector = selector<RootState, GridLayout | null>(
  gridPreset,
  (l: GridPresetStateRecord) => l.saved,
);
export const gridPresetCurrentSelector = selector<RootState, GridLayout | null>(
  gridPreset,
  (l: GridPresetStateRecord) => l.current,
);

export const gridPresetDefaultColumnsSelector = selector<RootState, GridLayout['columns']>(
  gridPreset,
  (l: GridPresetStateRecord) => l.defaultColumns,
);

export const gridPresetCurrentFiltersSelector = selector<RootState, GridLayout['filters']>(
  gridPresetCurrentSelector,
  (current: GridLayout | null) => current?.filters ?? NO_FILTERS,
);
export const gridPresetCurrentSortingSelector = selector<RootState, GridLayout['sorting']>(
  gridPresetCurrentSelector,
  (current: GridLayout | null) => current?.sorting ?? NO_SORTING,
);
// The unsaved parts of the working layout. Compared on this grid's part: what the preset has for columns this grid
// lacks is not shown, so it cannot change.
export const gridPresetChangesSelector = selector<RootState, GridLayoutChanges>(
  gridPresetSavedSelector,
  gridPresetCurrentSelector,
  gridPresetDefaultColumnsSelector,
  (saved: GridLayout | null, current: GridLayout | null, gridColumns: GridLayout['columns']) =>
    saved && current ? layoutChanges(layoutInGrid(saved, gridColumns), current) : NO_CHANGES,
);
export const isGridPresetChangedSelector = selector<RootState, boolean>(gridPresetChangesSelector, hasLayoutChanges);

// What saving stores: the working layout, with the current preset's parts about columns this grid lacks, so an
// "(all)" preset keeps another ERP's settings.
export const gridPresetToSaveSelector = selector<RootState, GridLayout | null>(
  gridPresetSavedSelector,
  gridPresetCurrentSelector,
  gridPresetDefaultColumnsSelector,
  (saved: GridLayout | null, current: GridLayout | null, gridColumns: GridLayout['columns']) =>
    current && (saved ? withPresetPartsNotInGrid(current, saved, gridColumns) : current),
);
